import dayjs from 'dayjs';
import {
  CalendarPosition,
  NavigationDirection
} from '../CalendarNavigationHandler.types';
import { NavigateCellsProps } from './utils.types';
import { getClosestCell } from './getClosestCell';

const convertToDurationInMinutes = (date: dayjs.Dayjs | Date) => {
  const convertedDate = dayjs(date);
  return convertedDate.hour() * 60 + convertedDate.minute();
};

export const navigateCells = ({
  direction,
  timeStep,
  position,
  event,
  calendars,
  startDate,
  endDate,
  timeSlotMin,
  timeSlotMax
}: NavigateCellsProps): CalendarPosition | void => {
  if (!position && !event) return;

  const calendar =
    calendars.filter(
      ({ calendarId }) =>
        calendarId === (position?.calendarId || event?.calendarId)
    )[0] || null;
  if (!calendar) return; // sanity

  const startPosition =
    position ||
    getClosestCell({
      timeStep,
      event
    });

  if (!startPosition) return;

  switch (direction) {
    case NavigationDirection.Up: {
      const newDate = startPosition.date.subtract(timeStep, 'minutes');

      if (newDate.isBefore(startPosition.date.startOf('day'))) return;

      const minTimeInMinutes = convertToDurationInMinutes(timeSlotMin);
      const newDateTimeInMinutes = convertToDurationInMinutes(newDate);

      if (minTimeInMinutes > newDateTimeInMinutes) return;

      return {
        ...startPosition,
        date: newDate
      };
    }

    case NavigationDirection.Down: {
      const newDate = startPosition.date.add(timeStep, 'minutes');

      if (newDate.isAfter(startPosition.date.endOf('day'))) return;

      const maxTimeInMinutes = convertToDurationInMinutes(timeSlotMax);
      const newDateTimeInMinutes = convertToDurationInMinutes(newDate);

      if (maxTimeInMinutes <= newDateTimeInMinutes) return;

      return {
        ...startPosition,
        date: newDate
      };
    }

    case NavigationDirection.Left: {
      const newDate = startPosition.date.subtract(1, 'day');

      if (newDate.isBefore(startDate)) {
        const currentCalendarIdx = calendars.indexOf(calendar);
        const previousCalendar =
          currentCalendarIdx !== 0 ? calendars[currentCalendarIdx - 1] : null;

        if (!previousCalendar) return;

        const lastDay = dayjs(endDate)
          .set('hours', newDate.hour())
          .set('minutes', newDate.minute());

        return {
          ...startPosition,
          date: lastDay,
          calendarId: previousCalendar.calendarId
        };
      }

      return {
        ...startPosition,
        date: newDate
      };
    }

    case NavigationDirection.Right: {
      const newDate = startPosition.date.add(1, 'day');

      if (newDate.startOf('day').isAfter(endDate)) {
        const currentCalendarIdx = calendars.indexOf(calendar);
        const nextCalendar =
          currentCalendarIdx < calendars.length - 1
            ? calendars[currentCalendarIdx + 1]
            : null;

        if (!nextCalendar) return;

        const firstDay = dayjs(startDate)
          .set('hours', newDate.hour())
          .set('minutes', newDate.minute());

        return {
          ...startPosition,
          date: firstDay,
          calendarId: nextCalendar.calendarId
        };
      }

      return {
        ...startPosition,
        date: newDate
      };
    }
  }
};
